import {
  NameType,
  Payload,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { COLORS } from "../components/recharts/ReCharts";
import { COLOR_CODES } from "../common";
import { LabelPosition } from "recharts/types/component/Label";

export interface ReChartData {
  [key: string]: number | string | Date | null | undefined;
}

export enum ChartType {
  Line,
  Bar,
  Area,
  Step,
}

export interface LegendKey {
  name?: string;
  stroke?: string;
  unit?: string;
  dataKey: string;
  type?: ChartType;
  yAxisId?: string;
  legendColor?: string;
  stackId?: string;
  isDash?: boolean;
  dateKey?: string;
  step?: string | boolean;
  [key: string]: any;
  // color: string;
}

export interface EditableSeriesPointChange {
  dataIndex: number;
  xValue: string | number | Date | null | undefined;
  oldValue: number;
  newValue: number;
  row: ReChartData;
}

export interface EditableSeriesConfig {
  enabled?: boolean;
  dataKey: string;
  yAxisId?: string;
  min?: number;
  max?: number;
  step?: number;
  handleSize?: number;
  handleColor?: string;
  onPointChange: (payload: EditableSeriesPointChange) => void;
  onPointChangeEnd?: (payload: EditableSeriesPointChange) => void;
}

export const BAR_RADIUS: number | [number, number, number, number] = [
  4, 4, 0, 0,
];

export function getColorList(length: number) {
  let colors = [];
  for (let i = 0; i < length; i++) {
    colors.push(Object.values(COLOR_CODES)[i % COLORS.length]);
  }
  return colors;
}

export enum BrushStart {
  Start,
  End,
}

interface BrushIndex {
  startIndex: number;
  endIndex: number;
}

export type CriticalPeriodKind =
  | "solar"
  | "evening_ramp"
  | "peak_demand"
  | "valley";

export interface CriticalPeriodDef {
  kind: CriticalPeriodKind;
  label: string;
  startHour: number;
  endHour: number;
  color?: string;
  stats?: Record<string, string>;
}

export interface ChartArguements {
  data: ReChartData[];
  legends: LegendKey[];
  legendBreakIndex?: number;
  syncid?: string;
  overflowLegend?: boolean;
  unit?: string;
  labelFontSize?: number;
  xDataKey: string;
  circleLegend?: boolean;
  xTick?: any;
  maxYsize?: number;
  disableLegendClick?: boolean;
  legendFontSize?: number;
  barCategoryGap?: number;
  xLabel?: string;
  onlyTitle?: boolean;
  xAxisLabelPosition?: LabelPosition;
  disableLegend?: boolean;
  yAxisWidth?: number;
  xaxisHeight?: number;
  xAxisAngle?: number;
  secondXAxisHeight?: number;
  xAxisTickMargin?: number;
  secondXAxisLabel?: string;
  secondXDataKey?: string;
  secondYAxisWidth?: number;
  yAxisLabel?: string;
  fontSize?: number;
  isTimeSlot?: boolean;
  blockDurationMinutes?: number;
  tooltipBoundary?: "viewport" | "chart";
  /** When true, hide hover tooltip as soon as pointer leaves chart area. */
  hideTooltipOnChartExit?: boolean;
  hidePrice?: boolean;
  showBrush?: boolean;
  xAxisLabelOffset?: number;
  xAxisInterval?: number | "auto";
  colorByPoint?: boolean;
  barColors?: string[];
  brushHeight?: number;
  rendexSecondXTick?: any;
  onBarClick?: (payload: any) => void;
  onLegendSelect?: any;
  brushIndex?: BrushIndex;
  brushStart?: BrushStart;
  secondYAxisLabel?: string;
  showSecondYAxis?: boolean;
  customTooltip?: any;
  /** Per-series display name resolver. Lets pages append metadata (e.g. dates)
   *  to series names while keeping built-in tooltip features (compare, hover sync). */
  resolveDisplayName?: (seriesName: string, dataPoint: any) => string;
  /** Per-series value resolver. Return formatted string to render in tooltip value slot
   *  (replaces default `toFixed(2)` + comma format). Receives raw value, seriesName,
   *  dataPoint, and matching legend. Return null/undefined to use default formatting. */
  resolveValue?: (
    value: number,
    seriesName: string,
    dataPoint: any,
    legend: any,
  ) => string | null | undefined;
  hiddenLegends?: string[];
  hiddenStats?: string[];
  dynamicMarginTop?: string;
  chartHeight?: string;
  enableMouseWheelZoom?: boolean;
  chartContainerHeight?: string;
  tooltip_key?: string;
  nameGap?: number; // Gap for axis names
  yMaxValue?: number; // Maximum value for Y axis
  tooltipFilter?: any;
  legendMaxHeight?: string;
  isPriceForecast?: boolean; // Flag to indicate if the chart is for price forecasting
  defaultSelectedLegends?: LegendKey[]; // Default selected legends for filtering
  showToolbox?: boolean; // Whether to show toolbox
  top?: number; // Top margin for grid
  bottom?: number; // Bottom margin for grid
  left?: number; // Left margin for grid
  yAxisDomain?: number[];
  secondYAxisDomain?: number[]; // Domain for the second Y axis
  isSmallLegend?: boolean;
  customLegendOrder?: string[]; // Custom order for legend items
  highlightedXValues?: (string | number)[]; // X-axis values to highlight
  enableClickSelection?: boolean; // Enable click-to-select functionality
  editableSeries?: EditableSeriesConfig | EditableSeriesConfig[]; // Optional draggable line-point editing (single or multiple series)
  isLoading?: boolean; // Optional: loading gate for empty-state rendering
  emptyStateDelayMs?: number; // Optional: legacy delay when isLoading is not provided
  /** Enable double-click on a chart point to activate compare mode. Defaults to true. */
  enableDoubleClickCompare?: boolean;
  /** Custom formatter for x-axis tick labels. When provided, overrides the default normalizeDisplayLabel. */
  xAxisTickFormatter?: (value: string) => string;
  /** Master on/off switch for critical-period highlight bands. When true and
   *  criticalPeriods is omitted, default solar/ramp/peak/valley bands are used.
   *  Auto-hides when x-axis is not intraday (Date/Month views). */
  showCriticalPeriods?: boolean;
  /** Override period definitions. Optional when showCriticalPeriods=true. */
  criticalPeriods?: CriticalPeriodDef[];
  /** Controlled active period kinds. When provided, the chart renders this instead
   *  of its own internal state — lets a parent persist the toggle across chart
   *  remounts (filter-driven key changes, Chart↔Table view switches). */
  activePeriodKinds?: Set<CriticalPeriodKind>;
  /** Called when the user toggles critical periods on/off inside the chart.
   *  Pair with activePeriodKinds to fully control the toggle. */
  onActivePeriodKindsChange?: (kinds: Set<CriticalPeriodKind>) => void;
  /** Hide the in-chart Critical Periods toggle button while still rendering the
   *  bands. Use when an external control (e.g. a filter-row toggle) owns the UI. */
  hideCriticalPeriodsButton?: boolean;
}

export interface ChartRecord {
  total_volume_mwh: number;
  weighted_average_price: number;
  [key: string]: any;
}
